import * as Toast from '@radix-ui/react-toast'
import { CircleAlert, CircleCheck, Info, LoaderCircle, TriangleAlert, X } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export type MessageTone = 'success' | 'error' | 'warning' | 'info' | 'loading'

interface MessageOptions {
  id?: string
  title?: string
  content: string
  tone?: MessageTone
  duration?: number
}

interface MessageItem extends Required<Omit<MessageOptions, 'id' | 'title' | 'duration'>> {
  id: string
  title?: string
  duration: number
  updatedAt: number
}

interface MessageApi {
  show: (options: MessageOptions) => string
  success: (content: string, title?: string) => string
  error: (content: string, title?: string) => string
  warning: (content: string, title?: string) => string
  info: (content: string, title?: string) => string
  loading: (content: string, title?: string) => string
  dismiss: (id: string) => void
}

const MessageContext = createContext<MessageApi | null>(null)
const defaultDuration: Record<MessageTone, number> = {success: 2200, error: 4600, warning: 3800, info: 2800, loading: 0}

export function MessageProvider({children}: {children: ReactNode}) {
  const [items, setItems] = useState<MessageItem[]>([])
  const recentMessagesRef = useRef(new Map<string, number>())
  const dismiss = useCallback((id: string) => setItems(current => current.filter(item => item.id !== id)), [])
  const show = useCallback((options: MessageOptions) => {
    const tone = options.tone ?? 'info'
    const id = options.id ?? crypto.randomUUID()
    const signature = `${id}:${tone}:${options.title ?? ''}:${options.content}`
    const now = Date.now()
    if (now - (recentMessagesRef.current.get(signature) ?? 0) < 30_000) return id
    recentMessagesRef.current.set(signature, now)
    const next: MessageItem = {
      id,
      title: options.title,
      content: options.content,
      tone,
      duration: options.duration ?? defaultDuration[tone],
      updatedAt: now,
    }
    setItems(current => {
      const existing = current.find(item => item.id === id)
      if (existing && existing.content === next.content && existing.title === next.title && existing.tone === next.tone) return current
      return [...current.filter(item => item.id !== id), next].slice(-2)
    })
    return id
  }, [])
  const api = useMemo<MessageApi>(() => ({
    show,
    success: (content, title) => show({content, title, tone: 'success'}),
    error: (content, title) => show({content, title, tone: 'error'}),
    warning: (content, title) => show({content, title, tone: 'warning'}),
    info: (content, title) => show({content, title, tone: 'info'}),
    loading: (content, title) => show({content, title, tone: 'loading'}),
    dismiss,
  }), [dismiss, show])

  return <MessageContext.Provider value={api}>
    {children}
    <Toast.Provider swipeDirection="right">
      {items.map(item => <MessageCard key={`${item.id}:${item.updatedAt}`} item={item} onDismiss={dismiss}/>) }
      <Toast.Viewport className="message-viewport" aria-label="系统消息" />
    </Toast.Provider>
  </MessageContext.Provider>
}

export function useMessage(): MessageApi {
  const value = useContext(MessageContext)
  if (!value) throw new Error('useMessage must be used within MessageProvider')
  return value
}

function MessageCard({item, onDismiss}: {item: MessageItem; onDismiss: (id: string) => void}) {
  const [paused, setPaused] = useState(false)
  const Icon = item.tone === 'success' ? CircleCheck : item.tone === 'error' ? CircleAlert : item.tone === 'warning' ? TriangleAlert : item.tone === 'loading' ? LoaderCircle : Info
  return <Toast.Root className={`message-card ${item.tone}`} duration={item.duration > 0 ? item.duration : Infinity} onOpenChange={open => !open && onDismiss(item.id)} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
    <span className="message-icon"><Icon/></span>
    <div>{item.title ? <><Toast.Title asChild><strong>{item.title}</strong></Toast.Title><Toast.Description asChild><p>{item.content}</p></Toast.Description></> : <Toast.Description asChild><p className="message-content-only">{item.content}</p></Toast.Description>}</div>
    <Toast.Close asChild><button type="button" aria-label="关闭消息"><X/></button></Toast.Close>
    {item.duration > 0 && <i className="message-timer" style={{animationDuration: `${item.duration}ms`, animationPlayState: paused ? 'paused' : 'running'}}/>}
  </Toast.Root>
}
